import { useEffect, useCallback, useRef } from 'react';

export interface KeyboardNavigationOptions {
  enabled?: boolean;
  gridNavigation?: boolean; // For table-like navigation
  wrapAround?: boolean; // Whether to wrap from last to first item
  onEscape?: () => void;
  onEnter?: (activeElement: Element) => void;
  onSpace?: (activeElement: Element) => void;
  itemSelector?: string; // CSS selector for navigable items
  containerRef?: React.RefObject<HTMLElement>;
}

export interface KeyboardNavigationState {
  activeIndex: number;
  focusNext: () => void;
  focusPrevious: () => void;
  focusFirst: () => void;
  focusLast: () => void;
  setActiveIndex: (index: number) => void;
}

const DEFAULT_ITEM_SELECTOR = '[data-keyboard-nav], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';

export function useKeyboardNavigation(options: KeyboardNavigationOptions = {}): KeyboardNavigationState {
  const {
    enabled = true,
    gridNavigation = false,
    wrapAround = true,
    onEscape,
    onEnter,
    onSpace,
    itemSelector = DEFAULT_ITEM_SELECTOR,
    containerRef,
  } = options;

  const activeIndexRef = useRef(0);
  const itemsRef = useRef<Element[]>([]);

  const getItems = useCallback(() => {
    const container = containerRef?.current || document;
    const elements = Array.from(container.querySelectorAll(itemSelector));
    itemsRef.current = elements.filter(el => {
      const style = window.getComputedStyle(el);
      return style.display !== 'none' && style.visibility !== 'hidden';
    });
    return itemsRef.current;
  }, [itemSelector, containerRef]);

  const focusElement = useCallback((index: number) => {
    const items = getItems();
    if (items.length === 0) return;

    // Clamp index to valid range
    const clampedIndex = Math.max(0, Math.min(index, items.length - 1));
    activeIndexRef.current = clampedIndex;

    const element = items[clampedIndex] as HTMLElement;
    if (element) {
      element.focus();
      element.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [getItems]);

  const focusNext = useCallback(() => {
    const items = getItems();
    if (items.length === 0) return;

    let nextIndex = activeIndexRef.current + 1;
    if (nextIndex >= items.length) {
      nextIndex = wrapAround ? 0 : items.length - 1;
    }
    focusElement(nextIndex);
  }, [focusElement, wrapAround, getItems]);

  const focusPrevious = useCallback(() => {
    const items = getItems();
    if (items.length === 0) return;

    let prevIndex = activeIndexRef.current - 1;
    if (prevIndex < 0) {
      prevIndex = wrapAround ? items.length - 1 : 0;
    }
    focusElement(prevIndex);
  }, [focusElement, wrapAround, getItems]);

  const focusFirst = useCallback(() => {
    focusElement(0);
  }, [focusElement]);

  const focusLast = useCallback(() => {
    const items = getItems();
    focusElement(items.length - 1);
  }, [focusElement, getItems]);

  const setActiveIndex = useCallback((index: number) => {
    focusElement(index);
  }, [focusElement]);

  // Handle keyboard events
  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (event: Event) => {
      // Type guard to ensure we have a KeyboardEvent
      if (!(event instanceof KeyboardEvent)) return;
      const { key, ctrlKey, metaKey, shiftKey } = event;
      
      // Don't interfere if user is typing in an input
      const target = event.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }

      switch (key) {
        case 'ArrowDown':
          event.preventDefault();
          if (gridNavigation && shiftKey) {
            // For grid navigation, Shift+ArrowDown could move down a row
            // This would need more complex logic based on the grid structure
            focusNext();
          } else {
            focusNext();
          }
          break;

        case 'ArrowUp':
          event.preventDefault();
          if (gridNavigation && shiftKey) {
            // For grid navigation, Shift+ArrowUp could move up a row
            focusPrevious();
          } else {
            focusPrevious();
          }
          break;

        case 'ArrowRight':
          if (gridNavigation) {
            event.preventDefault();
            focusNext();
          }
          break;

        case 'ArrowLeft':
          if (gridNavigation) {
            event.preventDefault();
            focusPrevious();
          }
          break;

        case 'Home':
          if (ctrlKey || metaKey) {
            event.preventDefault();
            focusFirst();
          }
          break;

        case 'End':
          if (ctrlKey || metaKey) {
            event.preventDefault();
            focusLast();
          }
          break;

        case 'Escape':
          if (onEscape) {
            event.preventDefault();
            onEscape();
          }
          break;

        case 'Enter':
          if (onEnter) {
            const items = getItems();
            const activeElement = items[activeIndexRef.current];
            if (activeElement) {
              event.preventDefault();
              onEnter(activeElement);
            }
          }
          break;

        case ' ':
          if (onSpace) {
            const items = getItems();
            const activeElement = items[activeIndexRef.current];
            if (activeElement) {
              event.preventDefault();
              onSpace(activeElement);
            }
          }
          break;
      }
    };

    const container = containerRef?.current || document;
    container.addEventListener('keydown', handleKeyDown);

    return () => {
      container.removeEventListener('keydown', handleKeyDown);
    };
  }, [enabled, gridNavigation, focusNext, focusPrevious, focusFirst, focusLast, onEscape, onEnter, onSpace, getItems, containerRef]);

  // Update active index when focus changes
  useEffect(() => {
    if (!enabled) return;

    const handleFocusIn = (event: Event) => {
      // Type guard to ensure we have a FocusEvent
      if (!(event instanceof FocusEvent)) return;
      const items = getItems();
      const focusedElement = event.target as Element;
      const index = items.indexOf(focusedElement);
      if (index !== -1) {
        activeIndexRef.current = index;
      }
    };

    const container = containerRef?.current || document;
    container.addEventListener('focusin', handleFocusIn);

    return () => {
      container.removeEventListener('focusin', handleFocusIn);
    };
  }, [enabled, getItems, containerRef]);

  return {
    activeIndex: activeIndexRef.current,
    focusNext,
    focusPrevious,
    focusFirst,
    focusLast,
    setActiveIndex,
  };
}

// Hook for managing focus visibility (show focus rings only when navigating with keyboard)
export function useFocusVisible() {
  useEffect(() => {
    let hadKeyboardEvent = false;

    const keydownHandler = () => {
      hadKeyboardEvent = true;
    };

    const mousedownHandler = () => {
      hadKeyboardEvent = false;
    };

    const focusHandler = (event: FocusEvent) => {
      const target = event.target as HTMLElement;
      if (hadKeyboardEvent) {
        target.classList.add('focus-visible');
      } else {
        target.classList.remove('focus-visible');
      }
    };

    const blurHandler = (event: FocusEvent) => {
      const target = event.target as HTMLElement;
      target.classList.remove('focus-visible');
    };

    document.addEventListener('keydown', keydownHandler);
    document.addEventListener('mousedown', mousedownHandler);
    document.addEventListener('focus', focusHandler, true);
    document.addEventListener('blur', blurHandler, true);

    return () => {
      document.removeEventListener('keydown', keydownHandler);
      document.removeEventListener('mousedown', mousedownHandler);  
      document.removeEventListener('focus', focusHandler, true);
      document.removeEventListener('blur', blurHandler, true);
    };
  }, []);
}